import {useCallback, useEffect, useRef, useState} from 'react';
import {createOrUpdatePropertyInstruction, getPropertyById, getPropertyConsumableTypes, getPropertyDurations,
    getPropertyJobTypes, updateProperty, updatePropertyDurations} from '../service/PropertyService';
import {getJobTypeOptions} from '../service/JobTypeService';
import {createJob, getPropertyJobs} from '../service/JobService';
import {loadPropertyRule, read, savePropertyRule} from '../service/AutoAssignService';
import {getWorkerCatalogs} from '../service/WorkerSettingsService';
import {getDirectoryEntriesByRole, getUsers} from '../service/UserService';
import {cleanerPayload, INSTRUCTION_TYPES, isValidDuration, loadPropertyInstruction, readPropertyResponse, sectionIsDirty} from './propertyEditorUtils';
import {normalizePropertyDetails, propertyDetailsError, propertyDetailsPayload} from './propertyDetailsModel';
import {normalizeGuestKeySettings} from './propertyGuestKeyModel';
import {emptyPropertyJobDraft, isValidJobDate, propertyJobErrors, propertyJobPayload} from './propertyJobModel';

const INSTRUCTION_KEYS = new Set(INSTRUCTION_TYPES.map(type => type.value));
const EMPTY_SECTION = {status: 'idle', value: null, baseline: null, error: '', notice: '', needsReload: false};
const EMPTY_JOB_CREATION = {open: false, error: '', uncertain: false, created: false, jobId: null, needsRefresh: false,
    refreshError: '', attemptedDate: '', verifiedJobs: null, verificationError: ''};

function initialSections() {
    return Object.fromEntries(['overview', 'durations', 'cleaner', 'jobs', ...INSTRUCTION_KEYS].map(key => [key, {...EMPTY_SECTION}]));
}

async function loadSection(propertyId, key) {
    if (key === 'overview') {
        const [property, jobTypes, consumableTypes, cleaningTypes] = await Promise.all([
            getPropertyById(propertyId).then(response => readPropertyResponse(response, 'Unable to load property details.')),
            getPropertyJobTypes().then(response => readPropertyResponse(response, 'Unable to load job types.')),
            getPropertyConsumableTypes().then(response => readPropertyResponse(response, 'Unable to load consumables.')),
            getJobTypeOptions(),
        ]);
        if (String(property.property_id) !== String(propertyId) || !Array.isArray(jobTypes) || !Array.isArray(consumableTypes)) {
            throw new Error('Unable to read property details.');
        }
        const value = normalizePropertyDetails(property);
        value.job_type = jobTypes.find(type => type.toLowerCase() === value.job_type.toLowerCase()) || value.job_type;
        return {value,
            jobTypes: jobTypes.filter(type => type.toLowerCase() !== 'task'), consumableTypes, cleaningTypes};
    }
    if (key === 'durations') {
        const data = await readPropertyResponse(await getPropertyDurations(propertyId));
        return {value: data.durations || []};
    }
    if (key === 'cleaner') {
        const [rule, catalogs, users] = await Promise.all([
            loadPropertyRule(propertyId), getWorkerCatalogs(), getUsers({roles: ['WORKER']}).then(read),
        ]);
        return {
            value: rule,
            skills: catalogs.skills || [],
            directory: Object.fromEntries(getDirectoryEntriesByRole(users, 'WORKER').map(entry => [String(entry.id), entry])),
        };
    }
    if (key === 'jobs') {
        const start = new Date();
        const end = new Date();
        start.setMonth(start.getMonth() - 2);
        end.setMonth(end.getMonth() + 2);
        const jobs = await readPropertyResponse(await getPropertyJobs(propertyId, start, end), 'Unable to load recent jobs.');
        if (!Array.isArray(jobs)) throw new Error('Unable to read recent jobs.');
        return {value: jobs};
    }
    return {value: await loadPropertyInstruction(propertyId, key)};
}

// The editor is keyed by property ID. Sections retain their drafts until the selected property changes.
export default function usePropertyEditor({property, isAdmin, activeKey, onSaved, guestKeyOpen = false}) {
    const [sections, setSections] = useState(initialSections);
    const [savingKey, setSavingKey] = useState(null);
    const [attachmentBusy, setAttachmentBusy] = useState({uploading: false, deleting: false});
    const [jobDraft, setJobDraft] = useState(emptyPropertyJobDraft);
    const [jobCreation, setJobCreation] = useState(EMPTY_JOB_CREATION);
    const [jobValidationAttempt, setJobValidationAttempt] = useState(0);
    const mounted = useRef(true);
    const requests = useRef({});
    const saveLock = useRef(false);
    const attachmentLock = useRef({uploading: false, deleting: false});
    const propertyId = property.property_id;

    useEffect(() => {
        mounted.current = true;
        return () => {
            mounted.current = false;
            requests.current = {};
        };
    }, []);

    const update = useCallback((key, updater) => {
        if (!mounted.current) return;
        setSections(current => ({...current, [key]: updater(current[key])}));
    }, []);

    const load = useCallback(async (key, preserveDraft = false) => {
        if (key === 'cleaner' && !isAdmin) return;
        const request = {};
        requests.current[key] = request;
        update(key, current => ({...current, status: 'loading', error: '', notice: ''}));
        try {
            const result = await loadSection(propertyId, key);
            if (!mounted.current || requests.current[key] !== request) return;
            update(key, current => {
                let value = result.value;
                if (preserveDraft && INSTRUCTION_KEYS.has(key) && current.value) {
                    const savedImages = new Map(result.value.images.map(image => [String(image.id), image]));
                    const previousIds = new Set(current.baseline.images.map(image => String(image.id)));
                    value = {...result.value, description: current.value.description,
                        images: current.value.images
                            .filter(image => savedImages.has(String(image.id)) || !previousIds.has(String(image.id)))
                            .map(image => savedImages.get(String(image.id)) || image),
                    };
                }
                return {...EMPTY_SECTION, ...result, value, baseline: result.value, status: 'ready'};
            });
        } catch (error) {
            if (!mounted.current || requests.current[key] !== request) return;
            update(key, current => ({...current, status: 'error', error: error.message || 'Unable to load this section.'}));
        }
    }, [propertyId, isAdmin, update]);

    const activeSection = sections[activeKey];
    useEffect(() => {
        if (activeSection?.status === 'idle') load(activeKey);
    }, [activeKey, activeSection?.status, load]);

    const change = useCallback((key, valueOrUpdater) => {
        update(key, current => ({...current,
            value: typeof valueOrUpdater === 'function' ? valueOrUpdater(current.value) : valueOrUpdater,
            notice: '',
        }));
    }, [update]);

    const setUploading = useCallback(value => {
        attachmentLock.current.uploading = value;
        if (mounted.current) setAttachmentBusy(current => ({...current, uploading: value}));
    }, []);
    const setDeleting = useCallback(value => {
        attachmentLock.current.deleting = value;
        if (mounted.current) setAttachmentBusy(current => ({...current, deleting: value}));
    }, []);

    function imagesDeleted(key, images) {
        update(key, current => {
            const remaining = new Set(images.map(image => String(image.id)));
            return {...current,
                value: {...current.value, images},
                // Deletes already happened on the server. Discarding the text draft cannot undo them.
                baseline: {...current.baseline, images: current.baseline.images.filter(image => remaining.has(String(image.id)))},
                notice: 'Attachment deleted.',
            };
        });
    }

    const busy = Boolean(savingKey || attachmentBusy.uploading || attachmentBusy.deleting || guestKeyOpen);
    const editable = activeKey !== 'jobs' && (!['overview', 'cleaner'].includes(activeKey) || isAdmin);
    const validationError = activeKey === 'overview' && activeSection?.value ? propertyDetailsError(activeSection.value) : '';
    const valid = !validationError && !activeSection?.checklistConflict && (activeKey !== 'durations' || (activeSection?.value?.length > 0
        && activeSection.value.every(duration => isValidDuration(duration.duration_minutes))));
    const overviewBlocked = activeKey === 'overview' && (jobCreation.needsRefresh || jobCreation.uncertain);
    const canReapplyFixedCleaner = activeKey === 'cleaner' && activeSection?.value?.assignment_mode === 'FIXED'
        && Boolean(activeSection.value.preferred_worker_id);
    const canSave = editable && !busy && !jobCreation.open && !overviewBlocked && activeSection?.status === 'ready'
        && !activeSection.needsReload && (sectionIsDirty(activeSection) || canReapplyFixedCleaner) && valid;
    const canCreateJob = isAdmin && !busy && !jobCreation.open && !jobCreation.needsRefresh && !jobCreation.uncertain
        && sections.overview.status === 'ready' && !sections.overview.needsReload && !sections.overview.checklistConflict;
    const jobValidationErrors = jobValidationAttempt ? propertyJobErrors(sections.overview, jobDraft) : {};

    function openJobCalendar() {
        if (!canCreateJob || saveLock.current || attachmentLock.current.uploading || attachmentLock.current.deleting) return;
        setJobValidationAttempt(current => current + 1);
        const firstError = Object.keys(propertyJobErrors(sections.overview, jobDraft))[0];
        if (firstError) return firstError;
        setJobCreation({...EMPTY_JOB_CREATION, open: true});
    }

    function closeJobCalendar() {
        if (!saveLock.current) setJobCreation(current => ({...current, open: false}));
    }

    function checklistConflict(error) {
        return error.status === 409 && error.body?.code === 'PROPERTY_CHECKLIST_CONFLICT'
            && String(error.body.property_id) === String(propertyId);
    }

    function showChecklistConflict(error) {
        update('overview', current => ({...current, checklistConflict: error.body,
            error: 'Property Tasks were changed elsewhere. Your draft has been kept. Load the latest Property Tasks, review them, and try again.'}));
    }

    async function refreshCreatedProperty() {
        const request = {};
        requests.current.overview = request;
        update('overview', current => ({...current, status: 'loading'}));
        try {
            const result = await readPropertyResponse(await getPropertyById(propertyId), 'Unable to refresh property details.');
            if (!mounted.current || requests.current.overview !== request) return;
            if (String(result.property_id) !== String(propertyId)) throw new Error('Unable to read the updated property.');
            const value = normalizePropertyDetails(result);
            value.job_type = sections.overview.jobTypes.find(type => type.toLowerCase() === value.job_type.toLowerCase()) || value.job_type;
            update('overview', current => ({...current, value, baseline: value, status: 'ready', error: '', notice: '',
                checklistConflict: null, needsReload: false}));
            setJobCreation(current => ({...current, needsRefresh: false, refreshError: ''}));
            onSaved?.(propertyId, 'overview', value);
        } catch (error) {
            if (!mounted.current || requests.current.overview !== request) return;
            update('overview', current => ({...current, status: 'ready'}));
            setJobCreation(current => ({...current, needsRefresh: true,
                refreshError: 'Job created successfully, but property details could not be refreshed. Retry the refresh before updating or creating another Job.'}));
        }
    }

    async function refreshRecentJobs() {
        // Invalidate stale jobs after creation or a cleaner save, including hidden sections.
        requests.current.jobs = {};
        if (sections.jobs.status === 'idle') update('jobs', () => ({...EMPTY_SECTION}));
        else await load('jobs');
    }

    async function confirmJobCreation(date) {
        if (!jobCreation.open || jobCreation.uncertain || !isAdmin || busy || saveLock.current
            || attachmentLock.current.uploading || attachmentLock.current.deleting) return;
        if (!isValidJobDate(date)) {
            setJobCreation(current => ({...current, error: 'Select today or a future date.'}));
            return;
        }
        if (Object.keys(propertyJobErrors(sections.overview, jobDraft)).length > 0) {
            setJobValidationAttempt(current => current + 1);
            setJobCreation(current => ({...current, open: false}));
            return;
        }
        const request = {};
        requests.current.createJob = request;
        saveLock.current = true;
        setSavingKey('job');
        setJobCreation(current => ({...current, error: '', attemptedDate: date}));
        let response;
        let writeConfirmed = false;
        try {
            response = await createJob(propertyJobPayload(sections.overview.value, jobDraft, date));
            if (!response.ok) await readPropertyResponse(response, 'Unable to create Job. Please retry.');
            writeConfirmed = true;
            // An OK response confirms the write even if its body cannot be read. Never retry that POST.
            const result = await response.json().catch(() => null);
            if (!mounted.current || requests.current.createJob !== request) return;
            const jobId = result?.cos_job_id == null ? null : String(result.cos_job_id);
            setJobCreation({...EMPTY_JOB_CREATION, created: true, jobId, needsRefresh: true});
            setJobDraft(emptyPropertyJobDraft());
            setJobValidationAttempt(0);
            setSavingKey('job-refresh');
            update('overview', current => ({...current, baseline: current.value, error: '', notice: ''}));
            await Promise.all([refreshCreatedProperty(), refreshRecentJobs()]);
        } catch (error) {
            if (!mounted.current || requests.current.createJob !== request) return;
            if (writeConfirmed) {
                setJobCreation(current => ({...current, open: false, created: true, needsRefresh: true,
                    refreshError: 'Job created successfully, but the page could not be refreshed. Retry the refresh.'}));
            } else if (checklistConflict(error)) {
                showChecklistConflict(error);
                setJobCreation(current => ({...current, open: false, error: ''}));
            } else {
                const uncertain = !response || response.status >= 500;
                setJobCreation(current => ({...current, uncertain, error: uncertain
                    ? 'The result could not be confirmed. A Job may have been created. Cancel and check jobs for this date before trying again.'
                    : (error.message || 'Unable to create Job. Please retry.')}));
            }
        } finally {
            saveLock.current = false;
            if (mounted.current && requests.current.createJob === request) setSavingKey(null);
        }
    }

    async function retryJobRefresh() {
        if (!jobCreation.needsRefresh || busy || saveLock.current) return;
        saveLock.current = true;
        setSavingKey('job-refresh');
        try {
            await Promise.all([refreshCreatedProperty(), refreshRecentJobs()]);
        } finally {
            saveLock.current = false;
            if (mounted.current) setSavingKey(null);
        }
    }

    async function verifyJobAttempt() {
        if (!jobCreation.uncertain || !jobCreation.attemptedDate || busy || saveLock.current) return;
        const request = {};
        requests.current.verifyJob = request;
        saveLock.current = true;
        setSavingKey('job-check');
        setJobCreation(current => ({...current, verifiedJobs: null, verificationError: ''}));
        try {
            // The attempted date may lie outside Recent Jobs' two-month window.
            const jobs = await readPropertyResponse(await getPropertyJobs(propertyId, jobCreation.attemptedDate, jobCreation.attemptedDate),
                'Unable to check jobs for this date. Please retry the check.');
            if (!Array.isArray(jobs)) throw new Error('Unable to read jobs for this date.');
            if (mounted.current && requests.current.verifyJob === request) {
                setJobCreation(current => ({...current, verifiedJobs: jobs}));
            }
        } catch (error) {
            if (mounted.current && requests.current.verifyJob === request) {
                setJobCreation(current => ({...current, verificationError: error.message}));
            }
        } finally {
            saveLock.current = false;
            if (mounted.current && requests.current.verifyJob === request) setSavingKey(null);
        }
    }

    async function save() {
        if (!canSave || saveLock.current || attachmentLock.current.uploading || attachmentLock.current.deleting) return;
        saveLock.current = true;
        setSavingKey(activeKey);
        const key = activeKey;
        const value = activeSection.value;
        let writeConfirmed = false;
        update(key, current => ({...current, error: '', notice: ''}));
        try {
            let saved;
            if (key === 'overview') {
                const result = await readPropertyResponse(await updateProperty(propertyDetailsPayload(value)), 'Unable to update property.');
                if (String(result.property_id) !== String(propertyId)) throw new Error('Unable to read the updated property. Reload saved values before trying again.');
                saved = normalizePropertyDetails({...value, ...result});
            } else if (key === 'durations') {
                const response = await updatePropertyDurations(propertyId, value.map(duration => ({
                    duration_type_id: duration.duration_type_id, duration_minutes: duration.duration_minutes,
                })));
                const result = await readPropertyResponse(response, 'Unable to save durations.');
                saved = result.durations || [];
            } else if (key === 'cleaner') {
                saved = await savePropertyRule(propertyId, cleanerPayload(value, activeSection.skills));
                await refreshRecentJobs();
            } else {
                const response = await createOrUpdatePropertyInstruction({
                    ...property, id: value.id, description: value.description, image_ids: value.images.map(image => image.id),
                }, key);
                if (!response.ok) await readPropertyResponse(response, 'Unable to save instructions.');
                writeConfirmed = true;
                // Create responses do not reliably contain the generated ID. Always read it back before another save.
                saved = await loadPropertyInstruction(propertyId, key);
                if (!saved.id && (value.description || value.images.length > 0)) {
                    throw new Error('The saved instruction could not be read back.');
                }
            }
            if (!mounted.current) return;
            update(key, current => ({...current, value: saved, baseline: saved, needsReload: false, notice: 'Changes saved.'}));
            onSaved?.(propertyId, key, saved);
        } catch (error) {
            if (!mounted.current) return;
            if (key === 'overview' && checklistConflict(error)) {
                showChecklistConflict(error);
                return;
            }
            update(key, current => ({...current,
                baseline: writeConfirmed ? value : current.baseline,
                needsReload: INSTRUCTION_KEYS.has(key),
                error: writeConfirmed
                    ? 'Changes were saved, but could not be refreshed. Reload saved values before editing again.'
                    : (error.message || 'Unable to save changes. Please retry.'),
            }));
        } finally {
            saveLock.current = false;
            if (mounted.current) setSavingKey(null);
        }
    }

    function applyGuestKeySettings(settings) {
        if (!mounted.current || !isAdmin) return;
        const patch = normalizeGuestKeySettings(settings);
        update('overview', current => current.value == null ? current : ({...current,
            value: {...current.value, ...patch}, baseline: {...current.baseline, ...patch},
        }));
        onSaved?.(propertyId, 'guest-key', patch);
    }

    const defaultCleaningTypeId = sections.overview.cleaningTypes?.find(option => option.is_default)?.id;
    const jobDraftIsDirty = jobDraft.check_in !== '' || jobDraft.checklists.length > 0
        || (jobDraft.cleaning_type_id != null && jobDraft.cleaning_type_id !== defaultCleaningTypeId);

    return {
        sections, activeSection, busy, savingKey, canSave, validationError, load, save, change, imagesDeleted, applyGuestKeySettings,
        jobDraft, jobCreation, jobValidationErrors, jobValidationAttempt, canCreateJob,
        openJobCalendar, closeJobCalendar, confirmJobCreation, retryJobRefresh, verifyJobAttempt,
        changeJobDraft: patch => setJobDraft(current => ({...current, ...patch})),
        clearJobDateError: () => setJobCreation(current => current.uncertain ? current : {...current, error: ''}),
        allowJobRetry: () => setJobCreation(current => current.verifiedJobs == null ? current : {...EMPTY_JOB_CREATION}),
        reloadPropertyTasks: () => update('overview', current => {
            if (!current.checklistConflict) return current;
            const {checklists, checklist_revision} = normalizePropertyDetails(current.checklistConflict);
            const patch = {checklists, checklist_revision};
            return {...current, value: {...current.value, ...patch}, baseline: {...current.baseline, ...patch},
                checklistConflict: null, error: '', notice: 'Latest Property Tasks loaded. Review them before updating.'};
        }),
        setUploading, setDeleting,
        setUploadError: (key, error) => update(key, current => ({...current, error})),
        dirtyKeys: Object.keys(sections).filter(key => key !== 'jobs' && (sectionIsDirty(sections[key])
            || (key === 'overview' && jobDraftIsDirty))),
    };
}
